import React, { useEffect, useState } from 'react';
import {
  Box,
  CardContent,
  Card,
  SvgIcon,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
  Button,
} from '@mui/material';
import { RemoveRedEye as VisibilityIcon, Delete } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import CustomTable from '../../General/CustomTable';
import { formatDate } from '../utils';
import { CONVERSATION_UI_LIST } from '../constants';
// import CustomButton from '../../../components/General/CustomButton';
import useStyles from '../styles';
import {
  ConversationSchema,
  TimePointSchema,
} from '../../../requests/api/apiTypes';
import { UserRole, UserType } from '../../../constants/user_types';
import { ReduxState } from '../../../types';
import apiClient from '../../../requests/api/apiClient';
import { FetchError } from '../../../views/types';

const ROWS_PER_PAGE = 5;

function ConversationsTab({
  // modalState,
  sectorId,
  chartName,
  setModalState,
  refreshCounter,
}: {
  // modalState: { open: boolean, conversationId: string },
  sectorId: string,
  chartName: string,
  setModalState: React.Dispatch<React.SetStateAction<{
    open: boolean,
    conversationId: string,
    timePointData: TimePointSchema,
  }>>,
  refreshCounter?: number,
  onConversationCreated?: () => void,
}) {
  const [conversationToDelete, setConversationToDelete] = useState<string>('');
  const { enqueueSnackbar } = useSnackbar();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [conversations, setConversations] = useState<ConversationSchema[]>([]);

  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [paginationParameters, setPaginationParameters] = useState({
    page: 0,
    rowsPerPage: ROWS_PER_PAGE,
  });
  const [totalConversations, setTotalConversations] = useState(0);

  const classes = useStyles();
  const currentUser = useSelector((state: ReduxState) => state.account.user);

  const loadConversations = async () => {
    setConversationsLoading(true);
    try {
      const { data } = await apiClient.conversation.conversationsList({
        page: paginationParameters.page,
        rowsPerPage: paginationParameters.rowsPerPage,
        sectorId,
        chartName,
      });
      setConversations(data.conversations || []);
      setTotalConversations(data.totalConversations || 0);
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    } finally {
      setConversationsLoading(false);
    }
  };

  const handleDeleteConversation = async () => {
    if (!conversationToDelete) return;

    setDeleting(true);
    try {
      await apiClient.conversation.conversationDelete(conversationToDelete);
      setDeleteDialogOpen(false);
      setConversationToDelete('');
      // Refresh the data by updating pagination parameters
      loadConversations();
    } catch (error) {
      console.error('Error deleting conversation:', error);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    loadConversations();
  }, [
    sectorId,
    chartName,
    refreshCounter,
    paginationParameters.page,
    paginationParameters.rowsPerPage,
  ]);

  // Reset to first page when external refresh counter changes (new conversation created)
  useEffect(() => {
    if (refreshCounter && refreshCounter > 0) {
      setPaginationParameters((prev) => ({ ...prev, page: 0 }));
    }
  }, [refreshCounter]);

  const openDeleteDialog = (conversationId: string) => {
    setConversationToDelete(conversationId);
    setDeleteDialogOpen(true);
  };

  const isAdmin = currentUser?.role === UserRole.ADMIN && currentUser?.type === UserType.CDTEC;

  const conversationData = {
    count: totalConversations,
    values: conversations.map((conversation) => ({
      id: conversation.conversationId,
      updatedAt: formatDate(conversation.updatedAt),
      updatedBy: conversation.updatedBy,
      lastComment: conversation.lastComment,
      farmName: conversation.farmName,
      chart: conversation.chart,
      seeMore: (
        <>
          <IconButton onClick={() => setModalState({
            open: true,
            conversationId: conversation.conversationId,
            timePointData: conversation.timePoint,
          })}>
            <SvgIcon fontSize="small">
              <VisibilityIcon />
            </SvgIcon>
          </IconButton>
      {isAdmin && (
        <IconButton
           onClick={() => openDeleteDialog(conversation.conversationId)}
          color="error">
          <Delete />
        </IconButton>
      )}
        </>
      ),
    })),
  };

  return (
    <Box className={classes.conversationTabContainer}>
      <Box className={classes.addCommentButton}>
        {/* TODO: Hacerlo responsive para que el boton no se superponga */}
        {/* <CustomButton
          buttonText='Agregar comentario al gráfico'
          color='blue'
          size='small'
          onClick={() => setModalState({ open: true, conversationId: '' })}
        /> */}
      </Box>
      <Card>
        <CardContent>
          {/* Arreglar este loading para que se vea bien */}
          {conversationsLoading ? (
            <CircularProgress size={'3rem'} />
          ) : (
            <CustomTable
              headers={CONVERSATION_UI_LIST.filter(
                (header) => !['sectorName', 'farmName', 'chart'].includes(header.key),
              )}
              rows={conversationData}
              paginationParameters={paginationParameters}
              setPaginationParameters={setPaginationParameters}
            />
          )}
        </CardContent>
        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            Confirmar eliminación
          </DialogTitle>
          <DialogContent>
            <Typography id="delete-dialog-description">
              ¿Estás seguro de que quieres eliminar esta conversación?
              Esta acción no se puede deshacer y eliminará todos los comentarios asociados.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteConversation}
              color="error"
              variant="contained"
              disabled={deleting}
            >
              {deleting ? <CircularProgress size={20} /> : 'Eliminar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    </Box>
  );
}

export default ConversationsTab;
